import { CalendarEvent } from '../../types/calendar';
import { getWeekDays, isToday, isSameDay } from '../../utils/calendar';
import { formatTime, formatDate } from '../../utils/time';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';

interface MobileWeekViewProps {
  date: Date;
  events: CalendarEvent[];
  onDateClick?: (date: Date, hour?: number) => void;
  onEventClick?: (event: CalendarEvent) => void;
}

export default function MobileWeekView({
  date,
  events,
  onDateClick,
  onEventClick,
}: MobileWeekViewProps) {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  
  // Validate date to prevent NaN issues
  if (!date || isNaN(date.getTime())) {
    return <div>Invalid date</div>;
  }
  
  const weekDays = getWeekDays(date);
  
  // Validate weekDays to ensure all dates are valid
  if (!weekDays || weekDays.length === 0 || weekDays.some(day => isNaN(day.year) || isNaN(day.month) || isNaN(day.day))) {
    return <div>Error calculating week days</div>;
  }

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
    
    return events
      .filter(event => {
        const eventStart = event.startTime instanceof Date 
          ? new Date(event.startTime) 
          : new Date(event.startTime);
        const eventEnd = event.endTime instanceof Date 
          ? new Date(event.endTime) 
          : new Date(event.endTime);
        
        // Event overlaps if: eventStart <= dayEnd AND eventEnd >= dayStart
        return eventStart.getTime() <= dayEnd.getTime() && eventEnd.getTime() >= dayStart.getTime();
      })
      .sort((a, b) => {
        const aStart = a.startTime instanceof Date ? a.startTime : new Date(a.startTime);
        const bStart = b.startTime instanceof Date ? b.startTime : new Date(b.startTime);
        return aStart.getTime() - bStart.getTime();
      });
  };


  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto scrollable">
        {/* Week Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 z-10 px-4 py-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">
              {formatDate(weekDays[0].date, 'short').replace(/, \d{4}$/, '')} - 
              {formatDate(weekDays[6].date, 'short').replace(/, \d{4}$/, '')}
            </h2>
          </div>
          
          {/* Day Headers */}
          <div className="flex gap-2">
            {weekDays.map((day) => {
              const isDateToday = isToday(day.date);
              const dayEvents = getEventsForDate(day.date);
              
              return (
                <div
                  key={`${day.year}-${day.month}-${day.day}`}
                  className={`flex-1 text-center py-2 rounded-landing min-w-[60px] ${
                    isDateToday ? `${tokens.button.secondary} ${tokens.text.accent}` : 'bg-gray-50 text-gray-700'
                  }`}
                >
                  <div className="text-xs font-medium mb-1">
                    {formatDate(day.date, 'long').split(',')[0].substring(0, 3)}
                  </div>
                  <div className={`text-lg font-bold ${isDateToday ? tokens.text.accent : 'text-gray-900'}`}>
                    {day.day}
                  </div>
                  {dayEvents.length > 0 && (
                    <div className="text-xs text-gray-600 mt-1">
                      {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Events List - Show events for selected day or today */}
        <div className="px-3 py-2">
          {weekDays.map((day) => {
            const dayEvents = getEventsForDate(day.date);
            if (dayEvents.length === 0) return null;

            return (
              <div key={`events-${day.year}-${day.month}-${day.day}`} className="mb-3">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  {isToday(day.date) ? 'Today' : formatDate(day.date, 'long')}
                </h3>
                <div className="space-y-1.5">
                  {dayEvents.map((event) => {
                    const startTime = new Date(event.startTime);
                    const endTime = new Date(event.endTime);
                    const isAllDay = event.isAllDay === true || event.isImportantDate || 
                      (startTime.getHours() === 0 && startTime.getMinutes() === 0 && 
                       endTime.getHours() === 23 && endTime.getMinutes() === 59);

                    return (
                      <div
                        key={event.id}
                        onClick={() => onEventClick?.(event)}
                        className="bg-white rounded-landing p-2.5 border-l-4 shadow-sm border-gray-200 active:bg-gray-50 min-h-[60px]"
                        style={{
                          borderLeftColor: event.members?.[0]?.memberColor || event.memberColor || '#3b82f6',
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-600 mb-1">
                              {isAllDay ? 'All Day' : formatTime(startTime)}
                            </div>
                            <h4 className="text-sm font-semibold text-gray-900 truncate">
                              {event.title}
                            </h4>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
